// Archivo: src/pages/AdminServiceCreate.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "../components/RichTextContent.css";
import { createService } from "../services/admin.service";

// Iconos (compatibles con el resto del proyecto)
const BackIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

const SaveIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
    />
  </svg>
);

const UploadIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
    />
  </svg>
);

export default function AdminServiceCreate() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo: null,
  });

  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (successMessage) setSuccessMessage("");
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Por favor selecciona un archivo de imagen válido");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("El archivo es demasiado grande. Máximo 5MB");
        return;
      }

      setFormData((prev) => ({ ...prev, logo: file }));

      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);

      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("El nombre del servicio es obligatorio");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage("");

    try {
      // Convertir el contenido del editor a HTML
      const rawContentState = convertToRaw(editorState.getCurrentContent());
      const htmlDescription = draftToHtml(rawContentState);

      const fd = new FormData();
      fd.append("name", formData.name.trim());
      fd.append("description", htmlDescription);
      if (formData.logo) fd.append("logo", formData.logo);

      await createService(fd);

      setSuccessMessage("Servicio creado exitosamente");

      //Volver al listado
      setTimeout(() => navigate("/admin"), 400);
    } catch (err) {
      setError(err.message || "Error al crear el servicio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Nuevo Servicio</h1>
          <p className="text-gray-600 mt-1">
            Crea un nuevo servicio que estará disponible en el portal público.
          </p>
        </div>
        <Link
          to="/admin"
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <BackIcon />
          <span className="ml-2">Volver</span>
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Logo del servicio
              </label>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-gray-300 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs text-center px-2">
                        Sin logo
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-grow">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro transition-colors">
                    <UploadIcon />
                    <span className="ml-2">
                      {formData.logo ? "Cambiar logo" : "Seleccionar logo"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoChange}
                      disabled={saving}
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Formatos: JPG, PNG, GIF. Máximo 5MB. Recomendado: 200x200px
                  </p>
                  {formData.logo && (
                    <p className="text-sm text-green-600 mt-1">
                      Nuevo logo: {formData.logo.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Nombre del servicio *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gob-azul-medio focus:border-transparent"
                placeholder="Ej: EDDI, SARH, etc."
                required
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descripción
              </label>
              <div className="border border-gray-300 rounded-lg min-h-[200px]">
                <Editor
                  editorState={editorState}
                  onEditorStateChange={setEditorState}
                  wrapperClassName="wrapper-class"
                  editorClassName="editor-class px-3"
                  toolbarClassName="toolbar-class"
                  placeholder="Describe el servicio, sus funciones y beneficios..."
                  readOnly={saving}
                  toolbar={{
                    options: [
                      "inline",
                      "blockType",
                      "list",
                      "textAlign",
                      // "colorPicker",
                      // "link",
                      "remove",
                      "history",
                    ],
                    inline: {
                      options: ["bold", "italic", "underline", "strikethrough"],
                    },
                    blockType: {
                      inDropdown: false,
                      options: ["Normal", "H1", "H2", "H3"],
                    },
                    list: {
                      options: ["unordered", "ordered"],
                    },
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <Link
                to="/admin"
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={saving || !formData.name.trim()}
                className="inline-flex items-center px-6 py-3 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <SaveIcon />
                    <span className="ml-2">Crear servicio</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
